import { CheckOutlined, InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { useMetadata } from '@api/useMetadata'
import { useOrders } from '@api/useOrders'
import { CarrierDepotRender } from '@components/DriverManagement/Common/DriverHeader'
import { ActionCell } from '@components/OrderDispatch/TripGrid/ActionBlock/ActionBlockCell'
import { Horizontal, NothingMessage, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { Col, Row, Select } from 'antd'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useScheduleContext } from '../Contexts/ScheduleContext'
import { NamePlate } from './NamePlate'
import { TripGridCell } from './TripGridCell/TripGridCell'
const { Option, OptGroup } = Select
import { driverSortOptions, getSortFn } from '@pages/OrderManagement/util'
import { AutoSizer, CellMeasurer, List } from 'react-virtualized'

export function TripGrid({
  triggerModal,
  triggerLinkWizard,
  linkWizardActive,
  linkAction,
  setOrderHistoryStoreId,
  setShowingDSUDrawer,
  setStatusUpdateRequest,
  tripTimeDisplay,
  config,
  ordersGridRef,
}) {
  const [driverSort, setDriverSort] = useState(driverSortOptions.Name)
  const {
    schedules,
    selectedDepots,
    selectedOrders,
    selectedDrivers,
    handleDriverChange,
    loadingSchedule,
    handleEquipmentChange,
    cellCache,
  } = useScheduleContext()
  const viewSchedules = schedules || []
  const { data: trailerMetadata } = useMetadata('trailer')
  const trailerNames = useMemo(() => trailerMetadata?.names, [trailerMetadata])
  const tractorNumbers = useMemo(() => trailerMetadata?.tractors, [trailerMetadata])
  const trailersWithConfigs = useMemo(() => trailerMetadata?.trailers_with_configs, [trailerMetadata])
  const [defaultDrivers, setDefaultDrivers] = useState([])
  const { sendCarrierOrderMutation } = useOrders()

  const options = useMemo(() => {
    const result = []
    const viewSchedulesSorted = viewSchedules.sort((a, b) => (a.driver_name > b.driver_name ? 1 : -1))
    viewSchedulesSorted.forEach((s, i) => {
      if (s.carrier_id) return
      result.push(
        <Option key={i} value={s.driver_name}>
          {s.driver_name}
        </Option>
      )
    })
    const carrierSchedules = viewSchedulesSorted.filter((s) => s.carrier_id)
    if (carrierSchedules.length > 0) {
      result.push(
        <OptGroup label='Carriers'>
          {carrierSchedules.map((s, i) => (
            <Option key={i} value={s.driver_name}>
              <CarrierDepotRender name={s.driver_name} carrier={s.carrier_name} />
            </Option>
          ))}
        </OptGroup>
      )
    }
    return result
  }, [viewSchedules])

  const filteredSchedules = useMemo(() => {
    if (!driverSort) {
      return viewSchedules
    }
    const sortFn = getSortFn(driverSort)
    let result =
      selectedDepots.length > 0 ? viewSchedules.filter((s) => selectedDepots.includes(s.home_location)) : viewSchedules
    if (linkWizardActive) {
      result = result.filter((d) => !d.trips.some((t) => t.is_linked))
    }
    result = selectedDrivers.length > 0 ? result.filter((s) => selectedDrivers.includes(s.driver_name)) : result

    if (selectedOrders?.length) result = result.filter((s) => s.trips?.some((t) => selectedOrders.includes(t.number)))

    if (defaultDrivers) result = result.filter((s) => defaultDrivers.includes(s.driver_name))
    // sort drivers by first name
    result = result.sort((a, b) => (a.driver_name > b.driver_name ? -1 : 1))
    return result.sort(sortFn)
  }, [selectedDepots, viewSchedules, linkWizardActive, selectedDrivers, defaultDrivers, driverSort, selectedOrders])

  const updateDefaultDrivers = (value) => {
    value?.length ? setDefaultDrivers(value) : setDefaultDrivers(null)
    handleDriverChange(value)
  }

  const { updateSetting, getSetting } = useUserSettings()
  const settingDriverSort = (newValue) => {
    setDriverSort(newValue)
    updateSetting('trip-grid-driver-sort', newValue)
    window.localStorage.setItem('trip-grid-driver-sort', newValue)
  }
  useEffect(() => {
    getSetting('trip-grid-driver-sort')
      .then((value) => {
        settingDriverSort(value || driverSortOptions.Name)
      })
      .catch(() => settingDriverSort(driverSortOptions.Name))
  }, [])
  useEffect(() => {
    getSetting('trip-grid-driver-name-filter')
      .then((value) => {
        if (!value) setDefaultDrivers(null)
        else value.includes(',') ? updateDefaultDrivers(value.split(',')) : updateDefaultDrivers([value])
      })
      .catch(() => setDefaultDrivers(null))
  }, [])

  // Calculate total width based on number of trips and action buttons
  const getTotalWidth = (schedule) => {
    const baseWidth = 330 // Base width for each trip cell
    const actionButtonWidth = 29 // Width of action button column
    const nameplateWidth = 450 // Fixed nameplate width
    const showActionColumn = config.multi_truck && !!schedule?.action_blocks && config.auto_trailer_swap

    const tripsWidth = (schedule?.trips?.length || 0) * baseWidth
    const actionButtonsWidth = showActionColumn ? (schedule?.trips?.length || 0) * actionButtonWidth : 0

    return tripsWidth + actionButtonsWidth + nameplateWidth
  }

  const maxWidth = useMemo(
    () => (filteredSchedules?.length ? Math.max(...filteredSchedules.map(getTotalWidth)) : 0),
    [filteredSchedules, config]
  )
  if (loadingSchedule) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--bg-1)',
        }}
      >
        <Texto style={{ margin: 'auto' }} appearance='primary' category='h1'>
          {' '}
          <LoadingOutlined />
        </Texto>
      </div>
    )
  }

  if (viewSchedules.length > 0) {
    return (
      <Vertical flex={1} className='trip-grid-container'>
        <Vertical flex={1} className='trip-grid-scroll'>
          <Vertical flex={1} className='virtualized-grid'>
            <AutoSizer>
              {({ height, width }) => (
                <div style={{ width, height, position: 'relative', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                    }}
                  >
                    <div style={{ width: maxWidth, minWidth: '100%' }}>
                      <TripHeader
                        options={options}
                        handleDriverChange={updateDefaultDrivers}
                        defaultDrivers={defaultDrivers}
                        schedules={viewSchedules}
                        multiTruck={config.multi_truck}
                        autoSwap={config.auto_trailer_swap}
                        settingDriverSort={settingDriverSort}
                        driverSort={driverSort}
                        driverSortOptions={driverSortOptions}
                      />
                      <List
                        width={maxWidth}
                        height={height - 50}
                        rowCount={filteredSchedules.length}
                        deferredMeasurementCache={cellCache}
                        rowHeight={cellCache.rowHeight}
                        overscanRowCount={10}
                        style={{
                          overflowY: 'auto',
                          overflowX: 'hidden',
                        }}
                        rowRenderer={({ index, key, style, parent }) => {
                          const schedule = filteredSchedules[index]
                          return (
                            <CellMeasurer cache={cellCache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
                              {({ registerChild }) => (
                                <div
                                  style={{
                                    ...style,
                                    width: getTotalWidth(schedule),
                                    minWidth: '100%',
                                  }}
                                  ref={registerChild}
                                >
                                  <TripGridRow
                                    ordersGridRef={ordersGridRef}
                                    siteConfig={config}
                                    scheduleIndex={index}
                                    schedule={schedule}
                                    triggerModal={triggerModal}
                                    linkWizardActive={linkWizardActive}
                                    triggerLinkWizard={triggerLinkWizard}
                                    linkAction={linkAction}
                                    setOrderHistoryStoreId={setOrderHistoryStoreId}
                                    setShowingDSUDrawer={setShowingDSUDrawer}
                                    setStatusUpdateRequest={setStatusUpdateRequest}
                                    multiTruck={config.multi_truck}
                                    autoSwap={config.auto_trailer_swap}
                                    trailerNames={trailerNames}
                                    tripTimeDisplay={tripTimeDisplay}
                                    sendCarrierOrderMutation={sendCarrierOrderMutation}
                                    trailersWithConfigs={trailersWithConfigs}
                                    tractorNumbers={tractorNumbers}
                                    handleEquipmentChange={handleEquipmentChange}
                                  />
                                </div>
                              )}
                            </CellMeasurer>
                          )
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </AutoSizer>
          </Vertical>
        </Vertical>
      </Vertical>
    )
  }

  return (
    <div className='no-resources'>
      <NothingMessage
        title='No Active Drivers'
        message='No Drivers Scheduled for this shift. You can manage driver schedules in the driver management screen.'
      />
    </div>
  )
}

function TripHeader({
  options,
  handleDriverChange,
  defaultDrivers,
  schedules,
  multiTruck,
  autoSwap,
  settingDriverSort,
  driverSort,
  driverSortOptions,
}) {
  return (
    <Row className='trip-header'>
      <Col className='depot-container vertical-flex-center' style={{ width: 450, flex: '0 0 450px' }}>
        <Horizontal verticalCenter className={'p-1'}>
          <Vertical style={{ overflowY: 'auto' }} flex={1.5} className={'m-0 p-0'}>
            <DriverMultiSelect
              options={options}
              handleDriverChange={handleDriverChange}
              defaultDrivers={defaultDrivers}
            />
          </Vertical>
          <Vertical className={'m-0 p-0'} flex={1} style={{ marginLeft: -10 }}>
            <Select
              className={'driver-sort-select'}
              optionLabelProp={'label'}
              style={{ overflowY: 'auto', width: '150px' }}
              size='small'
              value={driverSort}
              onChange={settingDriverSort}
              popupMatchSelectWidth={false}
            >
              {Object.entries(driverSortOptions).map(([key, value]) => (
                <Select.Option key={key} value={value} label={`Sort By | ${key}`} optionLabelProp='label'>
                  <Horizontal verticalCenter>
                    <div style={{ width: '20px' }}>
                      {driverSort === value && <CheckOutlined style={{ color: 'var(--theme-color-2)' }} />}
                    </div>
                    <Texto>{key}</Texto>
                  </Horizontal>
                </Select.Option>
              ))}
            </Select>
          </Vertical>
        </Horizontal>
      </Col>
      <Col flex='1'>
        <Row className='trip-grid-header' wrap={false}>
          <TripHeaderColumns schedules={schedules} multiTruck={multiTruck} autoSwap={autoSwap} />
        </Row>
      </Col>
    </Row>
  )
}

function DriverMultiSelect({ options, handleDriverChange, defaultDrivers }) {
  if (defaultDrivers)
    return (
      <Select
        mode='multiple'
        allowClear
        style={{ overflowY: 'auto', width: '100%' }}
        placeholder='Filter by Driver Name'
        onChange={handleDriverChange}
        defaultValue={defaultDrivers}
        size='small'
        data-cy='driver-search-input'
      >
        {options}
      </Select>
    )

  return (
    <Select
      mode='multiple'
      allowClear
      style={{ overflowY: 'auto', width: '90%' }}
      placeholder='Filter by Driver Name'
      onChange={handleDriverChange}
      size='small'
      data-cy='driver-search-input'
    >
      {options}
    </Select>
  )
}

const TripHeaderColumns = ({ schedules, multiTruck, autoSwap }) => {
  return schedules[0].trips?.map((_item, index) => {
    return (
      <Fragment key={index}>
        <Col flex='1 1 330px' style={{ minWidth: 330, height: 50 }} className='trip-col'>
          <Row style={{ flexWrap: 'nowrap', height: '100%' }}>
            <Col flex={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {`Trip ${index + 1}`}
            </Col>
          </Row>
        </Col>
        {multiTruck && autoSwap && (
          <Col
            flex='0 0 29px'
            style={{
              width: 29,
              minWidth: 29,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid var(--gray-300)',
            }}
          >
            <InfoCircleOutlined />
          </Col>
        )}
      </Fragment>
    )
  })
}

function TripGridRow({
  siteConfig,
  triggerModal,
  scheduleIndex,
  schedule,
  triggerLinkWizard,
  linkWizardActive,
  linkAction,
  setOrderHistoryStoreId,
  showingDSUDrawer,
  setShowingDSUDrawer,
  setStatusUpdateRequest,
  multiTruck,
  autoSwap,
  trailerNames,
  tripTimeDisplay,
  ordersGridRef,
  sendCarrierOrderMutation,
  trailersWithConfigs,
  tractorNumbers,
  handleEquipmentChange,
}) {
  const showActionColumn = multiTruck && !!schedule.action_blocks && autoSwap
  return (
    <Row wrap={false} className='trip-grid-row'>
      <Col style={{ width: 450, flex: '0 0 450px' }}>
        <NamePlate
          siteConfig={siteConfig}
          schedule={schedule}
          linkWizardActive={linkWizardActive}
          linkAction={linkAction}
          showingDSUDrawer={showingDSUDrawer}
          setShowingDSUDrawer={setShowingDSUDrawer}
          setStatusUpdateRequest={setStatusUpdateRequest}
          trailersWithConfigs={trailersWithConfigs}
          tractorNumbers={tractorNumbers}
          handleEquipmentChange={handleEquipmentChange}
        />
      </Col>
      <Col flex='1'>
        <Row wrap={false} className='row-trips'>
          {schedule.trips.map((t, i) => {
            return (
              <Fragment key={`${i}tripgridcell`}>
                <Col flex='1 1 330px' style={{ minWidth: 330 }} className='trip-col'>
                  <Row style={{ flexWrap: 'nowrap' }} className='h-100'>
                    <Col flex={1}>
                      <TripGridCell
                        ordersGridRef={ordersGridRef}
                        trip={t}
                        scheduleIndex={scheduleIndex}
                        tripIndex={i}
                        triggerModal={triggerModal}
                        schedule={schedule}
                        triggerLinkWizard={triggerLinkWizard}
                        setOrderHistoryStoreId={setOrderHistoryStoreId}
                        tripTimeDisplay={tripTimeDisplay}
                        config={siteConfig}
                        sendCarrierOrderMutation={sendCarrierOrderMutation}
                      />
                    </Col>
                  </Row>
                </Col>
                {showActionColumn && (
                  <Col
                    key={`${i}actioncell`}
                    flex='0 0 29px'
                    className='action-block vertical-flex h-100 overlay'
                    style={{
                      width: 29,
                      minWidth: 29,
                      background: 'var(--bg-1)',
                    }}
                  >
                    <ActionCell
                      trip={t}
                      slotIndex={i}
                      schedule={schedule}
                      actionBlock={schedule.action_blocks[i]?.[0]}
                      trailerNames={trailerNames}
                    />
                  </Col>
                )}
              </Fragment>
            )
          })}
        </Row>
      </Col>
    </Row>
  )
}
